/* eslint-disable @typescript-eslint/no-explicit-any */
import { fetchAccountTypes } from '@/accounts/helpers/account-types.helper';
import { formatAISummaryForEditor } from '@/voc/helpers/common.helper';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { defineStore, acceptHMRUpdate } from 'pinia';
import {
    createSavedView,
    deleteSavedView,
    fetchSavedView,
    fetchSavedViews,
    generateSavedViewSummary,
    updateSavedView,
} from '@/vuex/modules/saved-views/saved-views.api';
import { formatView, hasAccountTypesFilter, unmapFilters } from '@/vuex/modules/saved-views/saved-views.utils';
import { useVocItemsFiltersStore } from '@/stores/voc-items-filters.store';

// eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
function getVocItemsTableStore () { return require('@/stores/voc-items-table.store').useVocItemsTableStore(); } // TODO: replace when saved-views migrates to Pinia

function getEmptyFilters (type: string): Record<string, unknown> | undefined {
    const filtersStore = useVocItemsFiltersStore();
    if (type === 'feedback') return filtersStore.feedbackEmptyFilters;
    if (type === 'feedbackExplore') return filtersStore.feedbackExploreEmptyFilters;

    return undefined;
}

const promiseStore = new Map();

function getInitialState () {
    return {
        error: null as unknown,
        errorOne: null as unknown,
        loaded: false,
        fetching: false,
        fetchingOne: null as string | null,
        map: {
            feedback: [] as unknown[],
        } as Record<string, unknown[]>,
        counts: {
            feedback: {},
        } as Record<string, Record<string, unknown>>,
        savedView: {} as Record<string, unknown>,
        emptyTableGetter: {
            feedback: true,
        } as Record<string, boolean>,
        tableGetter: {
            feedback: true,
        } as Record<string, boolean>,
        tableSetter: {
            feedback: true,
        } as Record<string, boolean>,
    };
}

export const useSavedViewsStore = defineStore('savedViews', {
    state: getInitialState,
    getters: {
        savedViewsByType (state) {
            return (type = 'feedback') =>
                state.map[type]
                    ?.filter((view: any) => {
                        return view.createdAt > 1693402102312 || view.ownedByUser.username === '--system-user--';
                    })
                    .map((view: any) => {
                        return {
                            ...view,
                            count: state.counts[type]?.[view.id],
                        };
                    });
        },
        savedViewById (state) {
            return (id: string) => {
                let savedView: any = null;
                Object.keys(state.map).forEach((type) => {
                    if (!savedView) {
                        savedView = (state.map[type] as any[]).find((view) => view.id === id);
                        if (savedView) {
                            savedView = {
                                ...savedView,
                                count: state.counts[type]?.[id],
                            };
                        }
                    }
                });

                return savedView;
            };
        },
        activeSavedView (state): Record<string, unknown> | null {
            if (state.savedView) {
                return {
                    ...state.savedView,
                    count: (state.counts[state.savedView.type as string] as any)?.[state.savedView.id as string],
                } as Record<string, unknown>;
            }

            return null;
        },
        isViewDirty (state) {
            const filtersStore = useVocItemsFiltersStore();
            let isDirty = false;
            if (state.savedView.filters && state.savedView.type === 'feedback') {
                const activeFilters = filtersStore.allFilters;
                const currentFilters = [
                    ...Object.keys(state.savedView.filters as object),
                    ...Object.keys(activeFilters).filter(
                        (key: string) => !Object.keys(state.savedView.filters as object).includes(key),
                    ),
                ];
                currentFilters.forEach((filter) => {
                    const savedFilter =
                        (state.savedView.filters as any)[filter] !== undefined
                            ? JSON.parse(JSON.stringify((state.savedView.filters as any)[filter]))
                            : null;
                    const currentFilter =
                        activeFilters[filter] !== undefined
                            ? JSON.parse(JSON.stringify(activeFilters[filter]))
                            : null;
                    if (!isDirty && !isEqual(savedFilter, currentFilter)) {
                        isDirty = true;
                    }
                });
            }
            if (
                !isDirty &&
                state.savedView.type === 'feedback' &&
                (state.savedView.prompt || '') !== (filtersStore.prompt || '')
            ) {
                isDirty = true;
            }
            if (!isDirty && state.tableGetter[state.savedView.type as string]) {
                const savedViewTableConfig = {
                    ...(state.savedView.sort ? { sort: state.savedView.sort } : {}),
                    ...(state.savedView.columns ? { columns: state.savedView.columns } : {}),
                    ...(state.savedView.columnWidths ? { columnWidths: state.savedView.columnWidths } : {}),
                };
                if (!isDirty && !isEqual(savedViewTableConfig, state.tableGetter[state.savedView.type as string] ? getVocItemsTableStore().tableConfig : undefined)) {
                    isDirty = true;
                }
            }

            return isDirty;
        },
        allFeedbackId (): string | undefined {
            return (this as any).allFeedbackView?.id;
        },
        allFeedbackView (state) {
            let allFeedbackView: any = (state.map.feedback as any[]).find(
                (view) => view.name === 'All Feedback' && view.createdByUser.username === '--system-user--',
            );

            if (allFeedbackView) {
                allFeedbackView = {
                    ...allFeedbackView,
                    count: state.counts[allFeedbackView.type]?.[allFeedbackView.id],
                };
            }

            return allFeedbackView;
        },
        isSeededView (): (id: string) => boolean {
            return (id: string) => {
                return (this as any).savedViewById(id)?.createdByUser.username === '--system-user--';
            };
        },
        isGeneratingAnySummary (state) {
            return (type = 'feedback') => {
                return (state.map[type] as any[])?.some((view) => view.isGeneratingSummary);
            };
        },
    },
    actions: {
        setLoaded ({ loaded }: { loaded: boolean }) {
            this.loaded = loaded;
        },
        setFetching ({ isFetching }: { isFetching: boolean }) {
            this.fetching = isFetching;
        },
        setFetchingOne ({ id }: { id: string | null }) {
            this.fetchingOne = id;
        },
        setError ({ error }: { error: unknown }) {
            this.error = error as Record<string, unknown>;
        },
        setErrorOne ({ id, error }: { id: string | null; error: unknown }) {
            this.error = { id, error } as Record<string, unknown>;
        },
        setSavedViews ({ savedViews, type }: { savedViews: unknown[]; type: string }) {
            this.map[type] = savedViews;
        },
        setSavedView ({ savedView, version }: { savedView: any; version?: unknown }) {
            this.savedView = savedView;
            if (savedView.id && !version) {
                if (!this.map[savedView.type]) this.map[savedView.type] = [];
                const viewIndex = (this.map[savedView.type] as any[])?.findIndex(
                    (storedSavedView) => storedSavedView.id === savedView.id,
                );
                if (viewIndex === -1 || viewIndex === undefined) {
                    (this.map[savedView.type] as any[]).push(savedView);
                } else {
                    (this.map[savedView.type] as any[])[viewIndex] = savedView;
                }
            }
        },
        setSavedViewsCounts ({ countData, type }: { countData: Record<string, unknown>; type: string }) {
            this.counts[type] = countData;
        },
        updateSavedViewInMap ({ savedView }: { savedView: any }) {
            if (!this.map[savedView.type]) this.map[savedView.type] = [];
            const viewIndex = (this.map[savedView.type] as any[]).findIndex(
                (storedSavedView) => storedSavedView.id === savedView.id,
            );

            if (viewIndex === -1) {
                (this.map[savedView.type] as any[]).push(savedView);
            } else {
                (this.map[savedView.type] as any[])[viewIndex] = savedView;
            }
        },
        setSavedViewProperty ({ propertyKey, propertyValue }: { propertyKey: string; propertyValue: unknown }) {
            this.savedView[propertyKey] = propertyValue;
        },
        resetSavedView () {
            this.savedView = {};
        },
        async loadAll ({ reFetch = false, type }: { reFetch?: boolean; type: string } = { type: 'feedback' }) {
            if (!isEmpty(this.map[type]) && !reFetch && this.loaded) {
                return;
            }
            try {
                this.setFetching({ isFetching: true });
                const fetchedSavedViews = await fetchSavedViews(type);
                const hasAccountTypesFilterCheck = fetchedSavedViews.some((savedView: any) =>
                    hasAccountTypesFilter(savedView),
                );
                if (hasAccountTypesFilterCheck) {
                    await fetchAccountTypes();
                }

                const emptyFilters = getEmptyFilters(type);

                const savedViews = emptyFilters
                    ? fetchedSavedViews.map((savedView: any) => {
                        return formatView(savedView, {
                            emptyFilters,
                            emptyTable: this.emptyTableGetter[type] ? getVocItemsTableStore().feedbackEmptyTableConfig : undefined,
                        });
                    })
                    : fetchedSavedViews;

                this.setSavedViews({ savedViews, type });
                this.setLoaded({ loaded: true });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setFetching({ isFetching: false });
            }
        },
        async loadOne ({ reFetch = false, id, version }: { reFetch?: boolean; id: string; version?: unknown } = { id: '' }) {
            if ((this.savedView as any)?.id === id && !reFetch) return this.savedView;
            if (promiseStore.get(id)) return promiseStore.get(id);

            this.setFetchingOne({ id });
            this.setErrorOne({ id: null, error: null });

            promiseStore.set(
                id,
                fetchSavedView(id, version)
                    .then(async (fetchedView: any) => {
                        if (hasAccountTypesFilter(fetchedView)) {
                            await fetchAccountTypes();
                        }

                        return fetchedView;
                    })
                    .then((fetchedView: any) => {
                        const emptyFilters = getEmptyFilters(fetchedView.type);
                        const savedView = emptyFilters
                            ? formatView(fetchedView, {
                                emptyFilters,
                                emptyTable: this.emptyTableGetter[fetchedView.type] ? getVocItemsTableStore().feedbackEmptyTableConfig : undefined,
                            })
                            : fetchedView;
                        this.setSavedView({ savedView, version });

                        (this.map[savedView.type] as any[])?.forEach((view: any) => {
                            if (view.id !== savedView.id) {
                                delete view.applied;
                            }
                        });

                        return savedView;
                    })
                    .catch((error: unknown) => {
                        this.setErrorOne({ id, error });
                        throw error;
                    })
                    .finally(() => {
                        this.setFetchingOne({ id: null });
                        promiseStore.delete(id);
                    }),
            );

            return promiseStore.get(id);
        },
        async createSavedView ({ savedView: savedViewToSave }: { savedView: any }) {
            try {
                const newSavedView = {
                    ...savedViewToSave,
                    filters: unmapFilters(savedViewToSave.filters),
                };
                const createdSavedView = await createSavedView(newSavedView);
                if (hasAccountTypesFilter(createdSavedView)) {
                    await fetchAccountTypes();
                }

                const emptyFilters = getEmptyFilters(createdSavedView.type);

                const savedView: any = emptyFilters
                    ? formatView(createdSavedView, {
                        emptyFilters,
                        emptyTable: this.emptyTableGetter[createdSavedView.type] ? getVocItemsTableStore().feedbackEmptyTableConfig : undefined,
                    })
                    : createdSavedView;
                savedView.isNew = true;

                this.setSavedViews({
                    savedViews: [savedView, ...(this.map[savedView?.type] as any[])],
                    type: savedView?.type,
                });

                return savedView;
            } catch (error) {
                this.setError({ error });
                throw error;
            }
        },
        generateEmptySavedView ({ name, type, filters = {} }: { name: string; type: string; filters?: Record<string, unknown> }) {
            const emptyFilters = getEmptyFilters(type);

            const emptySavedView = emptyFilters
                ? formatView(
                    { name, type, filters },
                    {
                        emptyFilters,
                        emptyTable: this.emptyTableGetter[type] ? getVocItemsTableStore().feedbackEmptyTableConfig : undefined,
                    },
                )
                : { name, type };
            this.setSavedView({ savedView: emptySavedView });
        },
        async update (
            { savedView: savedViewToSave = null, persist = false, property = null }: { savedView?: any; persist?: boolean; property?: string | null } = {},
        ) {
            if (savedViewToSave) {
                this.setSavedView({ savedView: savedViewToSave });
            }

            let { savedView } = this;

            if (persist) {
                let updatedSavedView: any = { id: this.savedView.id };
                if (property === null) {
                    updatedSavedView = {
                        ...this.savedView,
                        filters: unmapFilters(this.savedView.filters as Record<string, unknown>),
                    };
                } else if (property === 'filters') {
                    updatedSavedView[property] = unmapFilters(this.savedView.filters as Record<string, unknown>);
                } else {
                    updatedSavedView[property] = this.savedView[property];
                }

                const returnedSavedView = await updateSavedView(updatedSavedView);
                if (hasAccountTypesFilter(returnedSavedView)) {
                    await fetchAccountTypes();
                }

                const emptyFilters = getEmptyFilters(returnedSavedView.type);

                savedView = emptyFilters
                    ? formatView(returnedSavedView, {
                        emptyFilters,
                        emptyTable: this.emptyTableGetter[returnedSavedView.type] ? getVocItemsTableStore().feedbackEmptyTableConfig : undefined,
                    })
                    : returnedSavedView;

                if ((savedView as any).id === this.savedView.id) {
                    if (this.savedView.isNew) {
                        (savedView as any).isNew = true;
                    }
                    if (this.savedView.applied) {
                        (savedView as any).applied = true;
                    }
                    if (this.savedView.isGeneratingSummary) {
                        (savedView as any).isGeneratingSummary = true;
                    }
                }
                this.setSavedView({ savedView: savedView as any });
                this.applySavedView({ filterParams: { persist: false, persistGlobal: false } });
            }

            if ((savedView as any)?.type) {
                const updatedSavedViews = (this.map[(savedView as any)?.type] as any[])?.map((view) => {
                    if (view.id === (savedView as any)?.id) {
                        return savedView;
                    }

                    return view;
                });
                this.setSavedViews({ savedViews: updatedSavedViews, type: (savedView as any)?.type });
            }

            return savedView;
        },
        applySavedView (
            { filterParams = {}, tableParams = {}, resetFilters = false, version }: { filterParams?: Record<string, unknown>; tableParams?: Record<string, unknown>; resetFilters?: boolean; version?: unknown } = {},
        ) {
            const filtersStore = useVocItemsFiltersStore();

            if (this.savedView.type === 'feedback' && this.savedView.filters) {
                filtersStore.updateFilters({
                    filters: this.savedView.filters as Record<string, unknown>,
                    ...(filterParams && { ...filterParams }),
                    reset: resetFilters,
                });
            }

            if (this.savedView.type === 'feedback') {
                filtersStore.updatePrompt({ prompt: this.savedView.prompt as string });
            }
            const updatedColumnState: Record<string, unknown> = {};
            if (this.tableSetter[this.savedView.type as string]) {
                let columns: unknown;
                if (this.savedView.columns) {
                    columns = this.savedView.columns;
                    if (!(this.savedView.columns as any[]).includes('source') && this.savedView.type === 'feedback') {
                        columns = ['source', ...(columns as any[])];
                    }
                    if (!(this.savedView.columns as any[]).includes('checkbox')) {
                        columns = ['checkbox', ...(columns as any[])];
                    }
                    updatedColumnState.columns = columns;
                } else {
                    columns = null;
                }

                const tableUpdates = {
                    type: this.savedView.type,
                    sort: this.savedView.sort ? this.savedView.sort : null,
                    columns,
                    columnWidths: this.savedView.columnWidths ? this.savedView.columnWidths : null,
                    ...(tableParams && { ...tableParams }),
                };
                // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
                const { useVocItemsTableStore } = require('@/stores/voc-items-table.store'); // TODO: replace when saved-views migrates to Pinia
                useVocItemsTableStore().update(tableUpdates);
            }

            const updatedSavedView = {
                ...this.savedView,
                ...updatedColumnState,
                applied: true,
            };

            this.setSavedView({ savedView: updatedSavedView, version });
        },
        async updateCurrentSavedView ({ property, value, persist = true }: { property: string; value: unknown; persist?: boolean }) {
            if (property === 'name' || property === 'description') {
                delete this.savedView.isNew;
                if (value === this.savedView[property]) return;
            }
            this.setSavedViewProperty({ propertyKey: property, propertyValue: value });
            const returnedSavedView = await this.update({ persist, property });

            return returnedSavedView;
        },
        async removeSavedView ({ savedViewId }: { savedViewId: string }) {
            try {
                const savedView = cloneDeep(this.savedViewById(savedViewId));
                await deleteSavedView(savedViewId);
                this.setSavedViews({
                    savedViews: (this.map[savedView.type] as any[]).filter((view: any) => view.id !== savedViewId),
                    type: savedView.type,
                });
            } catch (error) {
                this.setError({ error });
                throw error;
            }
        },
        async generateSavedViewSummary ({ feedbackIds, format, savedViewId }: { feedbackIds: string[]; format: string; savedViewId: string }) {
            try {
                const savedView = this.savedViewById(savedViewId);
                this.setSavedView({ savedView });

                await this.updateCurrentSavedView({
                    property: 'isGeneratingSummary',
                    value: true,
                    persist: false,
                });

                const summary = await generateSavedViewSummary(feedbackIds, format);
                const formattedSummary = formatAISummaryForEditor(summary, format);

                await this.updateCurrentSavedView({
                    property: 'summary',
                    value: {
                        count: feedbackIds.length,
                        userEdited: false,
                        summary: formattedSummary,
                    },
                });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                await this.updateCurrentSavedView({
                    property: 'isGeneratingSummary',
                    value: false,
                    persist: false,
                });
            }
        },
        updateItemCounts ({ type, countData }: { type: string; countData: Record<string, unknown> }) {
            this.setSavedViewsCounts({ countData, type });
        },
        updateItemCount ({ type, id, count }: { type: string; id: string; count: unknown }) {
            this.setSavedViewsCounts({
                countData: {
                    ...this.counts?.[type],
                    [id]: count,
                },
                type,
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSavedViewsStore, module.hot));
}
